import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  CopyCheck,
  SendHorizontal,
  ThumbsDown,
  ThumbsUp,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { TooltipIconButton } from "src/components/Chat/TooltipIconButton";

function ContentCopyable({
  content,
  disabled,
}: {
  content: string;
  disabled: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <TooltipIconButton
      onClick={(e) => handleCopy(e)}
      tooltip="Copy content"
      disabled={disabled}
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.div
            key="check"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            <CopyCheck className="text-green-500" />
          </motion.div>
        ) : (
          <motion.div
            key="copy"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
          >
            <Copy size={16} />
          </motion.div>
        )}
      </AnimatePresence>
    </TooltipIconButton>
  );
}

export function FeedbackDialog({
  open,
  onClose,
  onSubmit,
  feedbackType,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  feedbackType: "positive" | "negative" | null;
}) {
  const [comment, setComment] = useState("");

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const formJson = Object.fromEntries((formData as any).entries());
    const feedbackComment = formJson.comment || "";
    onSubmit(feedbackComment);
    handleClose();
  };

  const handleClose = () => {
    setComment("");
    onClose();
  };

  const isPositive = feedbackType === "positive";
  const title = "Share Feedback";
  const description = isPositive
    ? "We're glad you found this helpful! Please share what worked well for you."
    : "Help us improve by providing specific feedback about what could be better.";
  const buttonColor = "primary";
  const buttonText = "Submit";

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ paddingBottom: 0 }}>
        <DialogContentText>{description}</DialogContentText>
        <form onSubmit={handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            name="comment"
            label="Feedback"
            fullWidth
            multiline
            rows={3}
            //variant="standard"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <DialogActions>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color={buttonColor}>
              {buttonText}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BranchSwitcher({
  branch,
  branchOptions,
  onSelect,
  isLoading,
}: {
  branch: string | undefined;
  branchOptions: string[] | undefined;
  onSelect: (branch: string) => void;
  isLoading: boolean;
}) {
  if (!branchOptions || !branch) return null;
  const index = branchOptions.indexOf(branch);

  return (
    <div className="flex items-center gap-2">
      <IconButton
        size="small"
        onClick={() => {
          const prevBranch = branchOptions[index - 1];
          if (!prevBranch) return;
          onSelect(prevBranch);
        }}
        disabled={isLoading}
      >
        <ChevronLeft />
      </IconButton>
      <span className="text-sm">
        {index + 1} / {branchOptions.length}
      </span>
      <IconButton
        size="small"
        onClick={() => {
          const nextBranch = branchOptions[index + 1];
          if (!nextBranch) return;
          onSelect(nextBranch);
        }}
        disabled={isLoading}
      >
        <ChevronRight />
      </IconButton>
    </div>
  );
}

export function CommandBar({
  content,
  isHumanMessage,
  isAiMessage,
  isEditing,
  setIsEditing,
  onSubmitEdit,
  // handleRegenerate,
  isLoading,
  isLastMessage,
  onFeedback,
}: {
  content: string;
  isHumanMessage?: boolean;
  isAiMessage?: boolean;
  isEditing?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitEdit?: () => void;
  handleRegenerate?: () => void;
  isLoading: boolean;
  isLastMessage?: boolean;
  onFeedback?: (type: "positive" | "negative", comment?: string) => void;
}) {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<
    "positive" | "negative" | null
  >(null);

  if (isHumanMessage && isAiMessage) {
    throw new Error(
      "Can only set one of isHumanMessage or isAiMessage to true, not both."
    );
  }

  if (!isHumanMessage && !isAiMessage) {
    throw new Error(
      "One of isHumanMessage or isAiMessage must be set to true."
    );
  }

  if (
    isHumanMessage &&
    (isEditing === undefined ||
      setIsEditing === undefined ||
      onSubmitEdit === undefined)
  ) {
    throw new Error(
      "If isHumanMessage is true, all of isEditing, setIsEditing, and onSubmitEdit must be set."
    );
  }

  // const showEdit =
  //   isHumanMessage &&
  //   isEditing !== undefined &&
  //   !!setIsEditing &&
  //   !!onSubmitEdit;

  const showFeedback = isAiMessage && isLastMessage && !isLoading && onFeedback;

  const handleFeedbackClick = (type: "positive" | "negative") => {
    setFeedbackType(type);
    setFeedbackDialogOpen(true);
  };

  const handleFeedbackSubmit = (comment: string) => {
    if (feedbackType) {
      onFeedback?.(feedbackType, comment);
    }
  };

  if (isHumanMessage && isEditing && !!setIsEditing && !!onSubmitEdit) {
    return (
      <div className="flex items-center gap-2">
        <TooltipIconButton
          disabled={isLoading}
          tooltip="Cancel edit"
          onClick={() => {
            setIsEditing(false);
          }}
        >
          <XIcon />
        </TooltipIconButton>
        <TooltipIconButton
          disabled={isLoading}
          tooltip="Submit"
          onClick={onSubmitEdit}
        >
          <SendHorizontal />
        </TooltipIconButton>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <ContentCopyable content={content} disabled={isLoading} />
        {/* {!isAiMessage && !!handleRegenerate && (
          <TooltipIconButton
            disabled={isLoading}
            tooltip="Refresh"
            onClick={handleRegenerate}
          >
            <RefreshCcw />
          </TooltipIconButton>
        )} */}
        {/* TODO: add edit current issue is that parent checkpoints seems to be either wrong or contains the wrong values since it always contains all messages not only the ones that have been seen before the message that is being edited */}
        {/* {showEdit && (
          <TooltipIconButton
            disabled={isLoading}
            tooltip="Edit"
            onClick={() => {
              setIsEditing?.(true);
            }}
          >
            <Pencil />
          </TooltipIconButton>
        )} */}
        {showFeedback && (
          <>
            <TooltipIconButton
              disabled={isLoading}
              tooltip="Good response"
              aria-label="Good response"
              onClick={() => handleFeedbackClick("positive")}
            >
              <ThumbsUp />
            </TooltipIconButton>
            <TooltipIconButton
              disabled={isLoading}
              tooltip="Bad response"
              aria-label="Bad response"
              onClick={() => handleFeedbackClick("negative")}
            >
              <ThumbsDown />
            </TooltipIconButton>
          </>
        )}
      </div>
      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackType={feedbackType}
      />
    </>
  );
}
